import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { Send, Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { notificationService, customerService } from '../../services/api';
import { ModalGridContainer, ModalFormField } from '../common/ModalGrid';

const SendNotificationModal = ({
  open,
  onClose,
  contentType = null,
  contentId = null,
  prefilledTitleGr = '',
  prefilledTitleEn = '',
  prefilledBodyGr = '',
  prefilledBodyEn = '',
  prefilledTargetUserId = '',
  prefilledTargetUserLabel = ''
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  const [formData, setFormData] = useState({
    titleGr: '',
    titleEn: '',
    bodyGr: '',
    bodyEn: '',
    targetType: 'all',
    targetUserId: ''
  });

  useEffect(() => {
    if (open) {
      setFormData({
        titleGr: prefilledTitleGr,
        titleEn: prefilledTitleEn,
        bodyGr: prefilledBodyGr,
        bodyEn: prefilledBodyEn,
        targetType: prefilledTargetUserId ? 'specific' : 'all',
        targetUserId: prefilledTargetUserId || ''
      });
      setError('');
      setSuccess(false);

      if (!contentType && !prefilledTargetUserId) {
        fetchCustomers();
      }
    }
  }, [open, prefilledTitleGr, prefilledTitleEn, prefilledBodyGr, prefilledBodyEn, contentType, prefilledTargetUserId]);

  const fetchCustomers = async () => {
    try {
      setLoadingCustomers(true);
      const response = await customerService.getAll({ limit: 1000 });
      setCustomers(response.customers || []);
    } catch (err) {
      console.error('Error fetching customers:', err);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateForm = () => {
    if (contentType) {
      if (formData.targetType === 'specific' && !formData.targetUserId) {
        setError(t('notifications.selectUser'));
        return false;
      }
      return true;
    }

    if (!formData.titleGr.trim() || !formData.titleEn.trim()) {
      setError(t('notifications.titleRequired'));
      return false;
    }
    if (!formData.bodyGr.trim() || !formData.bodyEn.trim()) {
      setError(t('notifications.bodyRequired'));
      return false;
    }
    if (formData.targetType === 'specific' && !formData.targetUserId) {
      setError(t('notifications.selectUser'));
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError('');

      if (contentType) {
        await notificationService.sendForContent({
          contentType,
          contentId,
          targetType: formData.targetType,
          targetUserId: formData.targetUserId || undefined
        });
      } else {
        await notificationService.sendCustom({
          titleGr: formData.titleGr,
          titleEn: formData.titleEn,
          bodyGr: formData.bodyGr,
          bodyEn: formData.bodyEn,
          targetType: formData.targetType,
          targetUserId: formData.targetUserId || undefined
        });
      }

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.message || t('notifications.sendError'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {contentType
          ? t('notifications.sendContentNotification')
          : t('notifications.sendNotification')
        }
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {t('notifications.notificationSent')}
          </Alert>
        )}

        {!contentType && (
          <ModalGridContainer spacing={2} sx={{ mt: 1 }}>
            <ModalFormField>
              <TextField
                fullWidth
                label={t('notifications.titleGr')}
                value={formData.titleGr}
                onChange={(e) => handleChange('titleGr', e.target.value)}
                disabled={loading || success}
                required
              />
            </ModalFormField>

            <ModalFormField>
              <TextField
                fullWidth
                label={t('notifications.titleEn')}
                value={formData.titleEn}
                onChange={(e) => handleChange('titleEn', e.target.value)}
                disabled={loading || success}
                required
              />
            </ModalFormField>

            <ModalFormField fullWidth>
              <TextField
                fullWidth
                label={t('notifications.bodyGr')}
                value={formData.bodyGr}
                onChange={(e) => handleChange('bodyGr', e.target.value)}
                disabled={loading || success}
                multiline
                rows={3}
                required
              />
            </ModalFormField>

            <ModalFormField fullWidth>
              <TextField
                fullWidth
                label={t('notifications.bodyEn')}
                value={formData.bodyEn}
                onChange={(e) => handleChange('bodyEn', e.target.value)}
                disabled={loading || success}
                multiline
                rows={3}
                required
              />
            </ModalFormField>
          </ModalGridContainer>
        )}

        {contentType && (
          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Alert severity="info">
              {t('notifications.contentNotificationInfo', {
                type: contentType === 'video' ? t('videos.video') : t('stories.story')
              })}
            </Alert>
          </Box>
        )}

        {prefilledTargetUserId ? (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Alert severity="info" icon={false}>
              <strong>{t('notifications.sendTo')}:</strong> {prefilledTargetUserLabel || prefilledTargetUserId}
            </Alert>
          </Box>
        ) : (
          <FormControl component="fieldset" sx={{ mt: 3 }}>
            <FormLabel component="legend">{t('notifications.sendTo')}</FormLabel>
            <RadioGroup
              value={formData.targetType}
              onChange={(e) => handleChange('targetType', e.target.value)}
            >
              <FormControlLabel
                value="all"
                control={<Radio />}
                label={t('notifications.sendToAll')}
                disabled={loading || success}
              />
              <FormControlLabel
                value="specific"
                control={<Radio />}
                label={t('notifications.sendToSpecificUser')}
                disabled={loading || success}
              />
            </RadioGroup>
          </FormControl>
        )}

        {!prefilledTargetUserId && formData.targetType === 'specific' && (
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t('notifications.selectUser')}</InputLabel>
            <Select
              value={formData.targetUserId}
              onChange={(e) => handleChange('targetUserId', e.target.value)}
              label={t('notifications.selectUser')}
              disabled={loading || success || loadingCustomers}
            >
              {loadingCustomers ? (
                <MenuItem disabled>
                  {t('common.loading')}
                </MenuItem>
              ) : (
                customers.map((customer) => (
                  <MenuItem key={customer._id} value={customer._id}>
                    {customer.fullName} ({customer.email})
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          startIcon={<Close />}
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || success}
          startIcon={loading ? <CircularProgress size={20} /> : <Send />}
        >
          {loading ? t('common.sending') : t('common.send')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendNotificationModal;